import { ENTITLEMENTS } from '../../application-core/constants';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../../grafsync/grafsync';
import { makeCancelable } from '../utils/utils';
import WebWorker from '../utils/workerSetup';
import Warning from '../Warning';
import TemplateSelector from './TemplateSelector';
import TSVbuilder from './TSVbuilder';
import Associations from '../Associations';

class GenerateTSV extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isProcessing: false,
            isErrored: false,
            allowSave: true
        };
        this.cancellable = null;
    }

    doExport(exportName, resource, graph, format, options, template) {
        const properties = [...new Set(template.tsvColumnProperty)];
        const langs = [...new Set([this.props.defaultLang, ...template.tsvColumnLanguage])];
        this.cancellable = makeCancelable(GS.export(exportName, resource, graph, format, langs, properties));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    onLoadAssociations = (associations) => this.setState({ associations });

    processTSV = (data, template) => {

        this.worker = new WebWorker(this.props.tsvFunction);

        this.worker.addEventListener('message',
            (tsv) => {
                const url = window.URL.createObjectURL(new Blob([tsv.data], { type: 'text/tab-separated-values' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', this.props.resource.replace(/[^a-z0-9]/gi, '_').toLowerCase() + ".tsv");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isProcessing: false });
            },
            false);

        this.worker.onerror = () => {
            if (this.cancellable && !this.cancellable.isCancelled())
                this.setState({
                    isErrored: true,
                    isProcessing: false,
                    errorMessage: "An error occurred generating the TSV file."
                });
        };

        this.worker.postMessage({
            id: this.props.resource,
            data,
            mode: this.props.tsvMode,
            options: Object.assign({ associations: this.state.associations }, this.props.options, { defaultLang: this.props.defaultLang }),
            template: Object.assign({}, template)
        });
    };

    createTSV = async (template) => {
        try {
            this.setState({ isProcessing: true });
            const { exportName, resource, graph, options } = this.props;
            const data = await this.doExport(exportName, resource, graph, "application/ld+json", options, template).promise;
            this.processTSV(data, template);
        }
        catch (e) {
            if (this.cancellable && !this.cancellable.isCancelled())
                this.setState({
                    isErrored: true,
                    isProcessing: false,
                    errorMessage: "Could not download data for export."
                });
        }
    };

    applyOptions = (options) => this.setState({ options });

    onExitWarning = () => this.setState({ isErrored: false });

    onSelect = (template, allowSave) => this.setState({ template, allowSave });

    discardTemplate = () => this.setState({ template: null });

    render() {

        const { options, defaultLang, graph, resource, ontologyProjectAssociation, examples, entitlements } = this.props;
        const { isErrored, isProcessing, errorMessage, template, allowSave } = this.state;
        const canCreateTemplate = entitlements.includes(ENTITLEMENTS.CREATE_TSV_TEMPLATE);

        return (
            <div>
                {!template ?
                    <TemplateSelector
                        onSelect={this.onSelect}
                        graph={graph}
                        id={resource}
                        defaultLang={defaultLang} />
                    :
                    <Associations
                        graph={graph}
                        onLoad={this.onLoadAssociations}
                        ontologyProjectAssociation={ontologyProjectAssociation}
                        component={TSVbuilder}
                        id={resource}
                        isProcessing={isProcessing}
                        allowSave={canCreateTemplate && allowSave}
                        examples={examples}
                        onCreate={this.createTSV}
                        onDiscard={this.discardTemplate}
                        options={options}
                        defaultLang={defaultLang}
                        template={template} />
                }
                {isErrored && <Warning message={errorMessage} onExitWarning={this.onExitWarning} />}
            </div>
        );
    }
}

GenerateTSV.defaultProps = {
    options: {}
};

GenerateTSV.propTypes = {
    ontologyProjectAssociation: PropTypes.array,
    tsvFunction: PropTypes.func.isRequired,
    tsvMode: PropTypes.string.isRequired,
    options: PropTypes.object,
    resource: PropTypes.string.isRequired,
    exportName: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    examples: PropTypes.array,
    defaultLang: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default GenerateTSV;